(function (root) {
  const uncertainForms = new Set();
  const fields = {
    first_name: 'lead_first_name', last_name: 'lead_last_name', email_address: 'lead_email_address',
    phone_number: 'lead_phone_number', phone_number_extension: 'lead_phone_number_extension', company: 'lead_company',
    event_style: 'lead_event_style', event_description: 'lead_event_description', event_date: 'lead_event_date',
    start_time: 'lead_start_time', end_time: 'lead_end_time', guest_count: 'lead_guest_count',
    referral_source_id: 'lead_referral_source_id', referral_source_other: 'lead_referral_source_other'
  };
  const labels = { phone_number_extension: 'Phone extension', event_style: 'Event style', event_description: 'Nature of event', end_time: 'End time', referral_source_id: 'Referral source', referral_source_other: 'Referral details' };
  const escape = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  const date = (value) => { const [year, month, day] = value.split('-'); return year && month && day ? `${month}/${day}/${year}` : value; };
  const time = (value) => { const [hour, minute] = value.split(':').map(Number); return Number.isFinite(hour) && Number.isFinite(minute) ? `${hour % 12 || 12}:${String(minute).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}` : value; };

  function scriptUrl(base, formId) {
    const url = new URL(base);
    if (url.origin !== 'https://api.tripleseat.com' || url.pathname !== '/v1/leads/ts_script.js' || !/^\d+$/.test(String(formId))) throw new Error('The venue form configuration is invalid.');
    url.searchParams.set('lead_form_id', formId);
    return url.href;
  }

  function create({ host, baseUrl, context, onReady, onError, onSuccess, onResize, loadTimeout = 20000, submitTimeout = 30000 }) {
    let active = true;
    let ready = false;
    let submitting = false;
    let timer;
    let poll;
    let document;
    let provider;
    let frameWindow;
    let captcha;
    const frame = host.ownerDocument.createElement('iframe');
    frame.title = `Verification for ${context.label}`;
    frame.className = 'catering-booking-dialog__verification-frame';
    frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    const notifyError = (message, details = {}) => { if (active) onError(message, details); };
    const identityMatches = () => {
      try {
        const destination = new URL(provider.embed_url);
        return document.getElementById('lead_form_id')?.value === String(context.formId) && destination.origin === 'https://api.tripleseat.com' && destination.pathname === '/v1/leads/create.js' && destination.searchParams.get('id') === String(context.formId);
      } catch { return false; }
    };
    const markUncertain = () => {
      if (!active || !submitting) return;
      uncertainForms.add(String(context.formId));
      notifyError('We could not confirm whether your request was received. Please contact the Events team before submitting again.', { uncertain: true });
    };
    function resize() {
      if (!active || !ready) return;
      const challenge = Array.from(document.querySelectorAll('iframe')).find((item) => /\/bframe/.test(item.src) && item.getBoundingClientRect().height > 0 && frameWindow.getComputedStyle(item.parentElement).visibility !== 'hidden');
      frame.style.height = `${challenge ? 620 : captcha ? 120 : 0}px`;
      onResize?.(Boolean(captcha));
    }
    function initialize() {
      if (!active || ready) return;
      document = frame.contentDocument;
      frameWindow = frame.contentWindow;
      provider = frameWindow?.TS?.lead_form_embed;
      if (!provider?.form_selector || !document?.querySelector('#lead_submit') || !frameWindow.TS.jQuery) return;
      if (!identityMatches()) {
        clearInterval(poll); clearTimeout(timer);
        notifyError('The loaded form does not match this venue. Please retry.', { retryable: true });
        return;
      }
      if (['lead_first_name', 'lead_last_name', 'lead_email_address', 'lead_event_date', 'lead_start_time', 'lead_guest_count', 'lead_additional_information'].some((id) => !document.getElementById(id))) {
        clearInterval(poll); clearTimeout(timer);
        notifyError('This venue form is missing required booking fields. Please contact the Events team.', { retryable: true });
        return;
      }
      clearTimeout(timer);
      ready = true;
      captcha = document.querySelector('.g-recaptcha');
      const nativeForm = document.getElementById('tripleseat_embed_form');
      if (captcha) {
        let node = captcha;
        while (node && node !== document.body) {
          Array.from(node.parentElement.children).forEach((sibling) => { if (sibling !== node && !['STYLE', 'SCRIPT', 'LINK'].includes(sibling.tagName)) sibling.style.setProperty('display', 'none', 'important'); });
          node.style.setProperty('margin', '0', 'important');
          node.style.setProperty('padding', '0', 'important');
          node = node.parentElement;
        }
        nativeForm.style.visibility = 'visible';
      } else nativeForm.style.display = 'none';
      frameWindow.TS.custom_success_callback = () => {
        if (!active || !submitting || !identityMatches()) return;
        clearTimeout(timer); submitting = false;
        onSuccess();
      };
      provider.insert_lead_form_error_list = (errors, bookingErrors) => {
        if (!active || !submitting) return;
        clearTimeout(timer); submitting = false;
        const details = errors || bookingErrors || {};
        notifyError(Object.entries(details).map(([field, messages]) => `${field}: ${[].concat(messages).join(', ')}`).join(' ') || 'Please check your event details.', { fields: details });
      };
      onReady({
        hasCaptcha: Boolean(captcha),
        hasOptIn: Boolean(document.getElementById('lead_email_opt_in')),
        referrals: Array.from(document.querySelector('#lead_referral_source_id')?.options || []).map((option) => ({ value: option.value, label: option.text, disabled: option.disabled }))
      });
      resize();
    }
    function sync(data) {
      const notes = [`Booking context: ${context.label}`];
      const style = document.getElementById('lead_event_style');
      if (style?.tagName === 'SELECT' && !Array.from(style.options).some((option) => option.value === 'onpremise' && !option.disabled)) throw new Error('This venue form does not accept on-premise events. Please contact the Events team.');
      Object.entries(fields).forEach(([name, id]) => {
        let value = data[name] || '';
        if (name === 'event_date') value = date(value);
        if (name === 'start_time' || name === 'end_time') value = time(value);
        const control = document.getElementById(id);
        if (control && !control.disabled && (control.tagName !== 'SELECT' || Array.from(control.options).some((option) => option.value === value && !option.disabled))) {
          control.value = value;
        } else if (value) notes.push(`${labels[name] || name}: ${name === 'event_style' ? 'On-Premise Event' : value}`);
      });
      const optIn = document.getElementById('lead_email_opt_in');
      if (optIn) optIn.checked = data.email_opt_in === '1';
      const additional = document.getElementById('lead_additional_information');
      if (!additional) throw new Error('This form is missing the additional-information field. Please contact the Events team.');
      additional.value = [data.additional_information, ...notes].filter(Boolean).join('\n\n');
    }
    const api = {
      frame,
      get ready() { return active && ready; },
      get submitting() { return submitting; },
      submit(data) {
        if (!active || !ready) { notifyError('The venue form is still loading. Please retry once it is ready.'); return; }
        if (submitting || provider.is_form_submitting) return;
        if (uncertainForms.has(String(context.formId))) { notifyError('An earlier request to this venue has an unknown outcome. Please contact the Events team before submitting again.', { uncertain: true }); return; }
        if (!identityMatches()) { ready = false; notifyError('The loaded form does not match this venue. Please retry.', { retryable: true }); return; }
        if (captcha) {
          let response = '';
          try { response = frameWindow.grecaptcha?.getResponse?.() || ''; } catch { response = ''; }
          if (!response) { notifyError('Please complete the verification below before submitting.', { captcha: true }); return; }
        }
        try { sync(data); } catch (error) { notifyError(error.message); return; }
        submitting = true;
        timer = setTimeout(markUncertain, submitTimeout);
        try { provider.submit_lead(); } catch { markUncertain(); }
      },
      destroy() {
        if (submitting) uncertainForms.add(String(context.formId));
        active = false; clearTimeout(timer); clearInterval(poll); frame.remove();
      }
    };
    try {
      const source = scriptUrl(baseUrl, context.formId);
      host.replaceChildren(frame);
      const providerDocument = frame.contentDocument;
      providerDocument.open();
      providerDocument.write(`<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body{margin:0;padding:0;background:transparent}#tripleseat_embed_form{visibility:hidden;width:100%!important}#tripleseat_embed_form table{width:100%!important}body>h2{display:none}</style></head><body><script src="${escape(source)}"></script></body></html>`);
      providerDocument.close();
      poll = setInterval(() => { initialize(); resize(); }, 200);
      timer = setTimeout(() => { clearInterval(poll); notifyError('The venue form could not load. Your details are still here; please retry.', { retryable: true }); }, loadTimeout);
    } catch (error) { setTimeout(() => notifyError(error.message, { retryable: true }), 0); }
    return api;
  }
  root.CateringVenueProvider = { create, scriptUrl, escape };
})(typeof window === 'undefined' ? globalThis : window);
